// @flow

import _ from "underscore";
import ProductInfo from "./ProductInfo";
import {
    default as GlassProduct,
    MeasurementLine,
    NominalPoint,
    NominalAngle,
    Endpoint,
    AdasArea
} from "./GlassProduct";
import type {IntegerType} from "./model";
import ProductConfig from "./ProductConfig";

/**
 * Klass, mis teisendab tooteinfot erinevate esituste vahet.
 */
class ProductTransformer {

    productConfig: ProductConfig;

    constructor(productConfig: ProductConfig) {
        this.productConfig = productConfig;
    }

    /**
     *
     * @param {GlassProduct} g
     * @returns {ProductInfo}
     */
    glassProductToState(g: GlassProduct): ProductInfo {
        /**
         * @type {ProductInfo}
         */
        let state = new ProductInfo(this.productConfig);

        // Nominaalide info

        for (let i = 0; i < g.measurementLines.length; i++) {
            /**
             * @type {MeasurementLine}
             */
            let line = g.measurementLines[i];
            for (let n of line.nominals) {

                let name = this.fixPointName(n.name, i);
                let pointType = this.getPointType(name);
                let k = this.getPointOffset(name);

                switch (pointType) {
                    case "H":
                        if (k < 0 || k >= state.hudPoints) continue;
                        state.hudLine = i + 1;
                        state.hudYlemineTolerants2[k] = this.div10(n.upperTolerance2);
                        state.hudYlemineTolerants1[k] = this.div10(n.upperTolerance1);
                        state.hudNominaal[k] = this.div10(n.value);
                        state.hudAlumineTolerants1[k] = this.div10(n.lowerTolerance1);
                        state.hudAlumineTolerants2[k] = this.div10(n.lowerTolerance2);
                        state.hudv[k] = this.div10(n.distance);
                        state.hudKorrektsioon[k] = this.div10(n.correction);
                        break;

                    case "P":
                        state.pYlemineTolerants2[k] = this.div10(n.upperTolerance2);
                        state.pYlemineTolerants1[k] = this.div10(n.upperTolerance1);
                        state.pNominaal[k] = this.div10(n.value);
                        state.pAlumineTolerants1[k] = this.div10(n.lowerTolerance1);
                        state.pAlumineTolerants2[k] = this.div10(n.lowerTolerance2);
                        if (n.isAdas) state.adas = k;
                        state.pv[k] = this.div10(n.distance);
                        state.pKorrektsioon[k] = this.div10(n.correction);

                        state.pJoon[k] = i;
                        state.pNimi[k] = this.getPointName(k);
                        state.pOlemas[k] = true;
                        break;
                    default:
                        break;
                }
            }
            if (line.angles && line.angles.length > 0) {
                /**
                 * @type {NominalAngle}
                 */
                let angle = line.angles[0];
                state.angle = {
                    pYlemineTolerants2: this.div10(angle.upperTolerance2),
                    pYlemineTolerants1: this.div10(angle.upperTolerance1),
                    pNominaal: this.div10(angle.value),
                    pAlumineTolerants1: this.div10(angle.lowerTolerance1),
                    pAlumineTolerants2: this.div10(angle.lowerTolerance2)
                };
                let pointOffsets = [
                    this.getPointOffset(this.fixPointName(angle.point1, i), i),
                    this.getPointOffset(this.fixPointName(angle.point2, i), i),
                ];
                for (let p of pointOffsets) {
                    if (p != null) state.anglePoints[p] = true;
                }
            }

        }

        // Otspunktide info
        // Joonte info
        let k = 0;
        for (let i = 0; i < state.lineCount; i++) {
            for (let j = 0; j < 2; j++) {
                state.ex[k] = this.div10(g.measurementLines[i].endpoints[j].x);
                state.ey[k] = this.div10(g.measurementLines[i].endpoints[j].y);
                state.ez[k] = this.div10(g.measurementLines[i].endpoints[j].z);
                state.eKalleA[k] = this.div10(g.measurementLines[i].endpoints[j].slopeX);
                state.eKalleB[k] = this.div10(g.measurementLines[i].endpoints[j].slopeY);
                state.s[k] = this.div10(g.measurementLines[i].endpoints[j].printOffset);
                k++;
            }
            state.pikkus[i] = this.div10(g.measurementLines[i].lineLength);
        }
        state.scan = g.scanCode;
        state.mudeliNimi = g.description;
        state.klaasiPaksus = this.div10(g.glassThickness);
        state.profiilKlaasil = g.measuringProfileGlass;
        state.profiilTrykil = g.measuringProfilePrint;
        state.xnihe = state.ynihe = state.distants = 0;
        if (!isNaN(g.offsetX)) state.xnihe = this.div10(g.offsetX);
        if (!isNaN(g.offsetY)) state.ynihe = this.div10(g.offsetY);
        if (!isNaN(g.distance)) state.distants = this.div10(g.distance);
        state.xdiff = this.div10(g.diffX);
        state.ydiff = this.div10(g.diffY);
        state.enihe = this.div10(g.offsetE);

        state.joona = this.dupDivArray(g.lineLower);
        state.joony = this.dupDivArray(g.lineUpper);
        state.px = this.dupDivArray(g.pX);
        state.py = this.dupDivArray(g.pY);

        state.keeratud = g.rotatedLine;
        state.tolerants1k = this.div10(g.toleranceMiddle1);
        state.tolerants2k = this.div10(g.toleranceMiddle2);
        state.tolerants1s = this.div10(g.toleranceEdge1);
        state.tolerants2s = this.div10(g.toleranceEdge2);
        state.kood = g.codeVariant;
        state.peegeldatud = g.mirrored;
        state.rotated = g.rotated;

        if (!_.isArray(state.px) || state.px.length == 0) state.calcPV();
        state.hudx = state.calcHudV();
        //if (!_.isArray(state.joona) || state.joona.length == 0) ProductTransformer.calcJoonAY();

        state.keeratud_esimene = g.rotatedFirst;
        state.keeratud_viimane = g.rotatedLast;

        for (let i = 0; i < Math.min(state.adasAreasCount, (g.adasAreas != null ? g.adasAreas.length : 0)); i++) {
            state.adasarea_type[i] = ProductTransformer.numberToString(g.adasAreas[i].type);
            state.adasarea_distance[i] = this.div10(g.adasAreas[i].distance);
        }

        return state;
    }

    /**
     *
     * @param {ProductInfo} state
     * @returns {GlassProduct}
     */
    stateToGlassProduct(state: ProductInfo): GlassProduct {
        /**
         * @type {GlassProduct}
         */
        let g = new GlassProduct();

        g.lineCount = state.lineCount;

        g.measurementLines = [];

        for (let k = 0; k < state.pNominaal.length; k++) {
            if (!state.pOlemas[k]) continue;

            let n = new NominalPoint();
            n.upperTolerance2 = ProductTransformer.round10(state.pYlemineTolerants2[k]);
            n.upperTolerance1 = ProductTransformer.round10(state.pYlemineTolerants1[k]);
            n.value = ProductTransformer.round10(state.pNominaal[k]);
            n.lowerTolerance1 = ProductTransformer.round10(state.pAlumineTolerants1[k]);
            n.lowerTolerance2 = ProductTransformer.round10(state.pAlumineTolerants2[k]);
            n.isAdas = (state.adas === k);
            n.distance = ProductTransformer.round10(state.pv[k]);
            n.correction = ProductTransformer.round10(state.pKorrektsioon[k]);
            n.name = this.getPointName(k);

            let i = state.pJoon[k];
            if (!g.measurementLines[i]) {
                g.measurementLines[i] = new MeasurementLine();
                g.measurementLines[i].lineLength = ProductTransformer.round10(state.pikkus[i]);
            }
            g.measurementLines[i].nominals.push(n);

        }

        for (let k = 0; k < state.hudPoints; k++) {
            if (state.hudLine == null || state.hudv == null || state.hudv.length <= k) break;

            if (state.hudNominaal[k] == null) continue;

            let n = new NominalPoint();
            n.upperTolerance2 = ProductTransformer.round10(state.hudYlemineTolerants2[k]);
            n.upperTolerance1 = ProductTransformer.round10(state.hudYlemineTolerants1[k]);
            n.value = ProductTransformer.round10(state.hudNominaal[k]);
            n.lowerTolerance1 = ProductTransformer.round10(state.hudAlumineTolerants1[k]);
            n.lowerTolerance2 = ProductTransformer.round10(state.hudAlumineTolerants2[k]);
            n.isAdas = false;
            n.distance = ProductTransformer.round10(state.hudv[k]);
            n.correction = ProductTransformer.round10(state.hudKorrektsioon[k]);
            n.name = "H" + (k + 1);

            let i = state.hudLine - 1;
            if (!g.measurementLines[i]) {
                g.measurementLines[i] = new MeasurementLine();
                g.measurementLines[i].lineLength = ProductTransformer.round10(state.pikkus[i]);
            }
            g.measurementLines[i].nominals.push(n);
        }

        if (state.angle) {
            let a = new NominalAngle();
            a.upperTolerance2 = ProductTransformer.round10(state.angle.pYlemineTolerants2);
            a.upperTolerance1 = ProductTransformer.round10(state.angle.pYlemineTolerants1);
            a.value = ProductTransformer.round10(state.angle.pNominaal);
            a.lowerTolerance1 = ProductTransformer.round10(state.angle.pAlumineTolerants1);
            a.lowerTolerance2 = ProductTransformer.round10(state.angle.pAlumineTolerants2);
            a.name = "ANGLE";

            let anglePoints = [];
            for (let i = 0; i < state.anglePoints.length; i++) {
                if (state.anglePoints[i]) {
                    anglePoints.push(i);
                }
            }
            if (anglePoints.length > 0) a.point1 = this.getPointName(anglePoints[0]);
            if (anglePoints.length > 1) a.point2 = this.getPointName(anglePoints[1]);
            // Find out middle line number:
            let middleLine = Math.floor(state.lineCount / 2);
            g.measurementLines[middleLine].angles = [a];
        }

        let k = 0;
        for (let i = 0; i < state.lineCount; i++) {
            for (let j = 0; j < 2; j++) {
                let e = new Endpoint();
                e.x = ProductTransformer.round10(state.ex[k]);
                e.y = ProductTransformer.round10(state.ey[k]);
                e.z = ProductTransformer.round10(state.ez[k]);
                e.slopeX = ProductTransformer.round10(state.eKalleA[k]);
                e.slopeY = ProductTransformer.round10(state.eKalleB[k]);
                e.printOffset = ProductTransformer.round10(state.s[k]);

                g.measurementLines[i].endpoints.push(e);
                k++;
            }
        }

        g.scanCode = state.scan;
        g.description = state.mudeliNimi;
        g.glassThickness = ProductTransformer.round10(state.klaasiPaksus);
        g.measuringProfileGlass = state.profiilKlaasil;
        g.measuringProfilePrint = state.profiilTrykil;
        // (CStr(Round(maxx - minx, 1)) + ";" + CStr(Round(maxy - miny, 1)) + ";")
        g.diffX = ProductTransformer.round10(state.glass.maxx - state.glass.minx);
        g.diffY = ProductTransformer.round10(state.glass.maxy - state.glass.miny);
        g.offsetX = ProductTransformer.round10(state.xnihe);
        g.offsetY = ProductTransformer.round10(state.ynihe);
        g.distance = ProductTransformer.round10(state.distants);
        g.offsetE = ProductTransformer.round10(state.enihe);

        g.lineLower = ProductTransformer.round10Array(state.joona);
        g.lineUpper = ProductTransformer.round10Array(state.joony);
        g.pX = ProductTransformer.round10Array(state.px);
        g.pY = ProductTransformer.round10Array(state.py);

        g.rotatedLine = state.keeratud;
        g.toleranceMiddle1 = ProductTransformer.round10(state.tolerants1k);
        g.toleranceMiddle2 = ProductTransformer.round10(state.tolerants2k);
        g.toleranceEdge1 = ProductTransformer.round10(state.tolerants1s);
        g.toleranceEdge2 = ProductTransformer.round10(state.tolerants2s);
        g.codeVariant = state.kood;
        g.mirrored = state.peegeldatud;
        g.rotated = state.rotated;

        g.rotatedFirst = state.keeratud_esimene;
        g.rotatedLast = state.keeratud_viimane;

        for (let i = 0; i < state.adasAreasCount; i++) {
            g.adasAreas[i] = new AdasArea();
            g.adasAreas[i].type = ProductTransformer.stringToInteger(state.adasarea_type[i]);
            g.adasAreas[i].distance = ProductTransformer.round10(state.adasarea_distance[i]);
        }

        return g;
    }

    /**
     * @private
     * @param {Array} arr
     * @param {number} mult
     * @returns {Array}
     */
    static dupArray(arr, mult: number) {
        return _.map(arr, function (val) {
            if (val == null || _.isNaN(val)) return null;
            return val * mult;
        });
    }

    dupDivArray(arr) {
        return _.map(arr, function (val) {
            return this.div10(val);
        }.bind(this));
    }

    div10(val) {
        if (val == null || _.isNaN(val)) return val;
        return +(Math.round((val/10) + "e+1")  + "e-1");
    }

    /**
     * @private
     * @param val
     * @returns {number}
     */
    static round10(val) {
        if (val == null || _.isNaN(val)) return null;
        return Math.round(val * 10);
    }

    /**
     * @private
     * @param {Array} arr
     * @returns {Array}
     */
    static round10Array(arr) {
        return _.map(arr, function (val) {
            return ProductTransformer.round10(val);
        });
    }

    /**
     * Returns a point type based by its name.
     *
     * Mapping:
     *  * "Pi" -> "P"
     *  * "Hi" -> "H"
     *  * "ANGLE" -> "A"
     * @param name
     * @returns {string|null}
     */
    getPointType(name: string) :?string {
        if (name == null) return null;
        return name.substring(0, 1);
    }

    /**
     * Tagastab punkti offseti massiivides selle punkti joone ja nime baasilt
     * @protected
     * @param {string} name Punkti nimi: Pi, Hi, ANGLE
     * @returns {?number} Punkti offset punkti massiivides
     */
    getPointOffset(name: string): ?number {
        if (name == null) return null;

        let pointType = this.getPointType(name);

        switch (pointType) {
            case "P":
                // Pi
                let pName = name.substring(1);
                let d = parseInt(pName, 10);
                let i = 0;
                if (d < 50) i = d - 1;
                else i = this.productConfig.lineCount * 3 + Math.floor(d / 50) - 1;
                return i;
            case "H":
                // Hi
                return parseInt(name.substring(1), 10)-1;
            case "A":
                // ANGLE / ANGLEi
                if (name === "ANGLE") return 0;
                return parseInt(name.substring(5), 10)-1;
            default:
                return null;
        }
    }

    fixPointName(name: string, line: number) : string {
        if (name == null) return null;

        switch (name.substring(0, 1)) {
            case "A":
                if (name.startsWith("ANGLE")) return name;
                return "P" + (line*3 + 1).toString();
            case "B":
                return "P" + (line*3 + 2).toString();
            case "C":
                return "P" + (line*3 + 3).toString();
            case "H":
                if (name.substring(0, 3) === "HUD") return "H" + name.substring(3);
                // Intentional fallthrough
            default:
                return name;
        }
    }

    /**
     * Tagastab punkti ametliku nime offseti baasilt.
     * Eeldatud on, et punktid on järjestatud: [P1, P2, P3, ..., P7/13, P8/14, P9/15, P50, P100, ..., P300], kus n on joonte arv.
     * @param {number} offset Punkti asukoht kõikide nominaalide sorteeritud massiivis
     * @returns {string}
     */
    getPointName(offset) {
        if (offset < this.productConfig.lineCount*3) {
            return "P" + (offset + 1).toString();
        } else {
            let shift = offset - this.productConfig.lineCount*3;
            return "P" + (shift*50 + 50).toString();
        }
    }

    getOrderedNominals(lines: Array<MeasurementLine>): Array<NominalPoint|NominalAngle> {
        lines = this.fixNominals(lines);
        let names = this.getOrderedNominalNames();
        let namesMap = _.object(_.map(names, function (v, i) { return [v, i]; }));
        let items = [];
        for (let l of lines) {
            if (l.nominals != null) {
                for (let n of l.nominals) {
                    if (n.name in namesMap) items[namesMap[n.name]] = n;
                }
            }
            if (l.angles != null) {
                for (let a of l.angles) {
                    if (a.name in namesMap) items[namesMap[a.name]] = a;
                }
            }
        }
        for (let i = 0; i < names.length; i++) {
            if (items[i] == null) {
                if (names[i] === "ANGLE") items[i] = new NominalAngle({name: "ANGLE"});
                else items[i] = new NominalPoint({name: names[i]});
            }
        }
        return items;
    }

    /**
     * Tagastab nominaalide nimed järgmises järjestuses:
     *  * P1, P2, P3
     *  * P4, P5, P6
     *  * P7, P8, P9
     *  * P10, P11, P12 - kui on konfis määratud 5 rida
     *  * P13, P14, P15 - kui on konfis määratud 5 rida
     *  * P50, P100, ..., P300 - kui on konfis määratud lisapunktid
     *  * H1, H2, ..., H5 - kui on konfis määratud HUD punktid
     *  * ANGLE, ..., ANGLE - kui on konfis määratud nurgad
     */
    getOrderedNominalNames(): string[] {
        let names = [];
        for (let i = 1; i <= this.productConfig.lineCount*3; i++) {
            names.push("P" + i.toString());
        }
        for (let i = 50; i <= this.productConfig.addPoints*50; i += 50) {
            names.push("P" + i.toString());
        }
        for (let i = 1; i <= this.productConfig.hudPoints; i++) {
            names.push("H" + i.toString());
        }
        for (let i = 1; i <= this.productConfig.angleCount; i++) {
            names.push("ANGLE");
        }
        return names;
    }

    fixNominals(lines: Array<MeasurementLine>): Array<MeasurementLine> {
        for (let i = 0; i < lines.length; i++) {
            for (let m of lines[i].nominals) {
                switch (m.name.substring(0, 1)) {
                    case "A":
                        m.name = "P" + ((i*3)+1).toString();
                        break;
                    case "B":
                        m.name = "P" + ((i*3)+2).toString();
                        break;
                    case "C":
                        m.name = "P" + ((i*3)+3).toString();
                        break;
                    default:
                        break;
                }
                m.line = i;
            }
        }
        return lines;
    }

    static isCenterPoint(name: string): boolean {
        if (name.substring(0, 1) === "B") return true;
        if (name.substring(0, 1) !== "P") return false;
        let pnum = parseInt(name.substring(1), 10);
        return (pnum < 50 && (pnum-1) % 3 === 1);
    }

    static stringToInteger(value: ?string): ?IntegerType {
        if (value == null || value == "") return null;
        let numval = parseInt(value);
        return (_.isNaN(numval) ? null : numval);
    }

    static numberToString(value: ?IntegerType): ?string {
        return (value == null ? null : value.toString());
    }
}

export default ProductTransformer;
